'use client'

import { yupResolver } from '@hookform/resolvers/yup'
import { LineEnterprise } from '@qr-front/icons/line-enterprise'
import { GENERATOR_FORM_ANIMATION_DURATION } from 'features/generator/components/GeneratorFormSection'
import { useSyncFieldWithAtom } from 'hooks/useSyncFieldWithAtom'
import { useTranslations } from 'next-intl'
import { forwardRef, useImperativeHandle } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { validateContentForm } from 'utils/formUtils'

import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'

import {
	TContentFormHandle,
	TQrContentFormSection,
} from '../qrContentFormTypes'
import { useSyncFormSectionValidity } from '../useSyncFormSectionValidity'
import {
	getSummarySchema,
	summaryAtom,
	SummarySection,
	TSummaryFormField,
} from './SummarySection'

export const SUMMARY_FORM_ID = 'summary'

export const SummaryForm = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()

	const form = useForm<TSummaryFormField>({
		defaultValues: {
			summary: getStore().get(summaryAtom),
		},
		mode: 'onTouched',
		reValidateMode: 'onChange',
		resolver: yupResolver(getSummarySchema(t)),
	})

	const {
		setValue,
		handleSubmit,
		formState: { isValid },
	} = form

	useSyncFormSectionValidity({ formId: SUMMARY_FORM_ID, isValid })

	useSyncFieldWithAtom({
		atom: summaryAtom,
		setFieldValue: (value) => setValue('summary', value),
	})

	useImperativeHandle(
		ref,
		() => ({
			validate: () =>
				validateContentForm(
					handleSubmit,
					GENERATOR_FORM_ANIMATION_DURATION * 1.25,
				),
		}),
		[handleSubmit],
	)

	return (
		<FormProvider {...form}>
			<form>
				<SummarySection />
			</form>
		</FormProvider>
	)
})

SummaryForm.displayName = 'SummaryForm'

export const summaryFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: SUMMARY_FORM_ID,
	title: t('generator__content_form_section__about_company__title'),
	description: t('generator__content_form_section__about_company__description'),
	icon: <LineEnterprise />,
	Content: SummaryForm,
})
